import { ComponentType } from 'react'
import type { JSONSchemaType } from '../schema/resolveEffectiveSchema'
import { useFormContext } from './context'

function resolveName(namePrefix: string, name: string): string {
  if (!name && name !== '0') return namePrefix
  return namePrefix ? `${namePrefix}.${name}` : name
}

function getValueByPath(obj: Record<string, unknown>, path: string): unknown {
  if (!path) return obj
  const parts = path.split('.')
  let current: unknown = obj
  for (const part of parts) {
    if (current === null || current === undefined) return undefined
    if (typeof current === 'object') {
      if (Array.isArray(current)) {
        const idx = Number(part)
        current = isNaN(idx) ? undefined : current[idx]
      } else {
        current = (current as Record<string, unknown>)[part]
      }
    } else {
      return undefined
    }
  }
  return current
}

/**
 * HOC that connects a component to the dynamic-uniforms form context.
 * Compatible with the uniforms connectField API surface.
 */
export function connectField<P extends Record<string, any>>(
  Component: ComponentType<P>,
  options?: { initialValue?: boolean; kind?: 'leaf' | 'node' }
) {
  const Connected = (fieldProps: { name: string } & Partial<P>) => {
    const ctx = useFormContext()
    const fullName = resolveName(ctx.namePrefix, fieldProps.name)

    if (!ctx.bridge.hasField(fullName)) return null

    const schemaProps = ctx.bridge.getProps(fullName)
    const field = (schemaProps.field || {}) as JSONSchemaType
    const value = getValueByPath(ctx.model, fullName)
    const fieldError = ctx.errors.find(
      (e: { name: string; message: string }) => e.name === fullName
    )

    // Build base props, then layer in overrides. fieldProps.name is replaced with fullName.
    const { name: _fieldName, ...restFieldProps } = fieldProps

    const props = {
      // Schema props from bridge
      ...schemaProps,
      // Schema field properties (for compat with uniforms-style access)
      description: field.description,
      oneOf: field.oneOf,
      anyOf: field.anyOf,
      'ui-params': field['ui-params'],
      items: field.items,
      // Form state
      value,
      onChange: (val: unknown) => ctx.onChange(fullName, val),
      error: fieldError ? { message: fieldError.message } : undefined,
      disabled: ctx.disabled || !!schemaProps.disabled,
      readOnly: ctx.readOnly || !!schemaProps.readOnly,
      showInlineError: ctx.showInlineError,
      // Override with direct props (allows callers to override any prop)
      ...restFieldProps,
      // Always use the resolved full name
      name: fullName,
    }

    return <Component {...(props as unknown as P)} />
  }

  Connected.displayName = `Connected(${Component.displayName || Component.name || 'Component'})`

  return Connected
}

export { getValueByPath, resolveName }
